import { action, computed, makeObservable } from 'mobx'

import { LogisticsFilterType } from '~/client/src/shared/enums/LogisticsFilterType'
import KnownTranslatorKeys from '~/client/src/shared/localization/knownTranslatorKeys'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ILogisticItem from '~/client/src/shared/models/ILogisticItem'
import { RESET_ALL_FILTERS } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventContext from '~/client/src/shared/stores/EventStore/EventContext'
import InitialState from '~/client/src/shared/stores/InitialState'
import UIFilterInfo from '~/client/src/shared/stores/substates/UIFilterInfo'
import SuperFilterStore, {
  IRootOption,
} from '~/client/src/shared/stores/ui/SuperFilter.store'
import Guard from '~/client/src/shared/utils/Guard'

import BaseLogisticsList from './BaseLogisticsLists.store'
import BaseLogisticsMapStore from './ui/BaseLogisticsMap.store'

export type ISourceMap = { [optionId: string]: string[] }

export default class BaseLogisticsFilterStore extends SuperFilterStore {
  public get totalHint(): string {
    return Localization.translator.totalLogistics
  }

  public constructor(
    public readonly type: LogisticsFilterType,
    protected readonly state: InitialState,
    protected readonly getSourceMap: () => ISourceMap,
    protected readonly logisticsInstanceStore:
      | BaseLogisticsList
      | BaseLogisticsMapStore,
    protected readonly onShowChanged: (isShown: boolean, type: string) => void,
    protected readonly fieldsMap: { [filterType: string]: UIFilterInfo },
    getTypeCaption: (type: string) => string,
    public readonly getOptionName?: (
      optionId: string,
      filterType: string,
    ) => string,
    protected readonly onClickHandler?: () => void,
  ) {
    super(
      fieldsMap[type],
      type,
      state,
      KnownTranslatorKeys.seeXLogistics,
      getTypeCaption,
      onShowChanged,
      false,
      onClickHandler,
    )

    makeObservable(this)

    Guard.requireAll({ type, state, getSourceMap, logisticsInstanceStore })
  }

  @computed
  public get sourceMap(): ISourceMap {
    return this.getSourceMap()
  }

  @action.bound
  public onFilterActionRequest(eventContext: EventContext) {
    const [eventType] = eventContext.event

    switch (eventType) {
      case RESET_ALL_FILTERS:
        return this.setInitialFilterValues()
    }
  }

  public get isActive(): boolean {
    return !this.isLoading && this.areOptionsSelected
  }

  public get isHandleButtonActive(): boolean {
    return !this.isLoading && this.areOptionsSelected
  }

  public setInitialFilterValues() {
    if (!this.filter) {
      return
    }

    this.clickOnDeselectAll()
    this.clickOnApply()
  }

  @computed
  public get optionsTree(): IRootOption[] {
    const options = Object.keys(this.sourceMap)

    return [
      {
        id: this.type,
        name: this.typeCaption,
        options: options.map(option => ({
          id: option,
          name: this.getOptionName?.(option, this.type) || option,
          instancesIds: this.sourceMap[option],
        })),
      },
    ]
  }

  @action.bound
  public clickOnApply() {
    this.handleApply()
    this.onClickHandler?.()
  }

  public filterOptionInstances = (ids: string[]) => {
    return (this.allFilteredInstances as Array<ILogisticItem>).filter(c =>
      ids.includes(c.id),
    )
  }

  public clearNotPresentOptions() {
    const validOptionIds = new Set(this.allOptionIds)
    Array.from(this.selectedOptions.keys()).forEach(optionId => {
      if (!validOptionIds.has(optionId)) {
        this.selectedOptions.delete(optionId)
      }
    })
  }

  @computed
  private get allFilteredInstances(): unknown[] {
    const { getFilteredCollectionExcludeFilter } = this.logisticsInstanceStore
    return getFilteredCollectionExcludeFilter([this.type])
  }

  private get areOptionsSelected(): boolean {
    return (
      this.selectedOptions.size &&
      this.allOptionIds.some(optionId => !this.selectedOptions.get(optionId))
    )
  }
}
